import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { JSBI, Percent, Router, SwapParameters, Trade, TradeType } from '@uniswap/sdk'
import { useMemo } from 'react'
import { BIPS_BASE, DEFAULT_DEADLINE_FROM_NOW, INITIAL_ALLOWED_SLIPPAGE } from '../constants'
import { getTradeVersion, useV1TradeExchangeAddress } from '../data/V1'
import { useTransactionAdder } from '../state/transactions/hooks'
import { calculateGasMargin, getRouterContract, isAddress, shortenAddress } from '../utils'
import isZero from '../utils/isZero'
import v1SwapArguments from '../utils/v1SwapArguments'
import { shouldUseFeeOnTransferMethods } from './TaxTokenTrades'
import { getTaxAdjustedAmounts } from './Trades'
import { useActiveWeb3React } from './index'
import { useV1ExchangeContract } from './useContract'
import useENS from './useENS'
import { Version } from './useToggledVersion'
import i18next from '../i18n'

export enum SwapCallbackState {
  INVALID,
  LOADING,
  VALID
}

interface SwapCall {
  contract: Contract
  parameters: SwapParameters
}

interface SuccessfulCall {
  call: SwapCall
  gasEstimate: BigNumber
}

interface FailedCall {
  call: SwapCall
  error: Error
}

type EstimatedSwapCall = SuccessfulCall | FailedCall

/**
 * Returns the swap calls that can be used to make the trade
 * @param trade trade to execute
 * @param allowedSlippage user allowed slippage
 * @param deadline the deadline for the trade
 * @param recipientAddressOrName
 */
function useSwapCallArguments(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  deadline: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): SwapCall[] {
  const { account, chainId, library } = useActiveWeb3React()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  const v1Exchange = useV1ExchangeContract(useV1TradeExchangeAddress(trade), true)

  return useMemo(() => {
    const tradeVersion = getTradeVersion(trade)
    if (!trade || !recipient || !library || !account || !tradeVersion || !chainId) return []

    const contract: Contract | null =
      tradeVersion === Version.v2 ? getRouterContract(chainId, library, account) : v1Exchange
    if (!contract) {
      return []
    }

    const swapMethods = []
    const requiresFeeOnTransfer = shouldUseFeeOnTransferMethods(trade)

    switch (tradeVersion) {
      case Version.v2:
        // For tax tokens, only use fee-on-transfer methods
        if (requiresFeeOnTransfer) {
          if (trade.tradeType === TradeType.EXACT_INPUT) {
            // Only use fee-on-transfer method for tax tokens - no fallback
            swapMethods.push(
              Router.swapCallParameters(trade, {
                feeOnTransfer: true,
                allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
                recipient,
                ttl: deadline
              })
            )
          } else {
            // For EXACT_OUTPUT with tax tokens, we still need to use regular method
            // since fee-on-transfer methods don't support exact output
            swapMethods.push(
              Router.swapCallParameters(trade, {
                feeOnTransfer: false,
                allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
                recipient,
                ttl: deadline
              })
            )
          }
        } else {
          // Regular tokens - existing logic
          swapMethods.push(
            Router.swapCallParameters(trade, {
              feeOnTransfer: false,
              allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
              recipient,
              ttl: deadline
            })
          )

          if (trade.tradeType === TradeType.EXACT_INPUT) {
            swapMethods.push(
              Router.swapCallParameters(trade, {
                feeOnTransfer: true,
                allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
                recipient,
                ttl: deadline
              })
            )
          }
        }
        break
      case Version.v1:
        swapMethods.push(
          v1SwapArguments(trade, {
            allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
            recipient,
            ttl: deadline
          })
        )
        break
    }
    return swapMethods.map(parameters => ({ parameters, contract }))
  }, [account, allowedSlippage, chainId, deadline, library, recipient, trade, v1Exchange])
}

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useSwapCallback(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  deadline: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): { state: SwapCallbackState; callback: null | (() => Promise<string>); error: string | null } {
  const { account, chainId, library } = useActiveWeb3React()

  const swapCalls = useSwapCallArguments(trade, allowedSlippage, deadline, recipientAddressOrName)

  const addTransaction = useTransactionAdder()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !library || !account || !chainId) {
      return { state: SwapCallbackState.INVALID, callback: null, error: i18next.t('missingDependencies') as string }
    }
    if (!recipient) {
      if (recipientAddressOrName !== null) {
        return { state: SwapCallbackState.INVALID, callback: null, error: i18next.t('invalidRecipient') as string }
      } else {
        return { state: SwapCallbackState.LOADING, callback: null, error: null }
      }
    }

    const tradeVersion = getTradeVersion(trade)

    return {
      state: SwapCallbackState.VALID,
      callback: async function onSwap(): Promise<string> {
        const estimatedCalls: EstimatedSwapCall[] = await Promise.all(
          swapCalls.map(call => {
            const {
              parameters: { methodName, args, value },
              contract
            } = call
            const options = !value || isZero(value) ? {} : { value }

            console.log('Attempting to call method:', methodName, 'with args:', args)
            console.log('Contract address:', contract.address)
            console.log('Trade details:', {
              inputAmount: trade.inputAmount.toSignificant(6),
              outputAmount: trade.outputAmount.toSignificant(6),
              tradeType: trade.tradeType,
              path: trade.route.path.map(token => token.symbol)
            })
            
            return contract.estimateGas[methodName](...args, options)
              .then(gasEstimate => {
                console.log('Gas estimate successful for method:', methodName, 'Gas:', gasEstimate.toString())
                return {
                  call,
                  gasEstimate
                }
              })
              .catch(gasError => {
                console.debug('Gas estimate failed, trying eth_call to extract error', call)

                return contract.callStatic[methodName](...args, options)
                  .then(result => {
                    console.debug('Unexpected successful call after failed estimate gas', call, gasError, result)
                    return { call, error: new Error(i18next.t('gasEstimateError')) }
                  })
                  .catch(callError => {
                    console.debug('Call threw error', call, callError)
                    console.log('Full callError object:', callError)
                    console.log('callError.reason:', callError.reason)
                    console.log('callError.message:', callError.message)
                    console.log('callError.data:', callError.data)
                    console.log('callError.code:', callError.code)
                    
                    let errorMessage: string

                    // Handle JSON-RPC errors specifically
                    if (callError.message && callError.message.includes('Internal JSON-RPC error')) {
                      console.log('JSON-RPC error detected, checking for underlying reason...')
                      if (callError.data && callError.data.message) {
                        console.log('Underlying error:', callError.data.message)
                        errorMessage = i18next.t('networkErrorWithMessage', { message: callError.data.message })
                      } else {
                        errorMessage = i18next.t('jsonRpcNetworkError')
                      }
                    } else {
                      // Handle other specific errors
                      switch (callError.reason) {
                        case 'UniswapV2Router: INSUFFICIENT_OUTPUT_AMOUNT':
                        case 'UniswapV2Router: EXCESSIVE_INPUT_AMOUNT':
                          errorMessage = i18next.t('slippageError')
                          break
                        default:
                          errorMessage = i18next.t('tradeFailedWithReason', { reason: callError.reason || callError.message || 'Unknown error' })
                      }
                    }
                    return { call, error: new Error(errorMessage) }
                  })
              })
          })
        )

        // a successful estimation is a bignumber gas estimate and the next call is also a bignumber gas estimate
        const successfulEstimation = estimatedCalls.find(
          (el, ix, list): el is SuccessfulCall =>
            'gasEstimate' in el && (ix === list.length - 1 || 'gasEstimate' in list[ix + 1])
        )

        if (!successfulEstimation) {
          const errorCalls = estimatedCalls.filter((call): call is FailedCall => 'error' in call)
          if (errorCalls.length > 0) throw errorCalls[errorCalls.length - 1].error
          throw new Error(i18next.t('unexpectedError'))
        }

        const {
          call: {
            contract,
            parameters: { methodName, args, value }
          },
          gasEstimate
        } = successfulEstimation

        return contract[methodName](...args, {
          gasLimit: calculateGasMargin(gasEstimate),
          ...(value && !isZero(value) ? { value, from: account } : { from: account })
        })
          .then((response: any) => {
            const inputSymbol = trade.inputAmount.currency.symbol
            const outputSymbol = trade.outputAmount.currency.symbol
            
            // Get tax-adjusted amounts for accurate transaction description
            const taxAdjustedAmounts = getTaxAdjustedAmounts(trade)
            
            // Use tax-adjusted amounts if available, otherwise use original amounts
            const inputAmount = taxAdjustedAmounts.adjustedInputAmount?.toSignificant(3) || trade.inputAmount.toSignificant(3)
            const outputAmount = taxAdjustedAmounts.adjustedOutputAmount?.toSignificant(3) || trade.outputAmount.toSignificant(3)

            console.log('Transaction summary amounts:', {
              originalInput: trade.inputAmount.toSignificant(3),
              originalOutput: trade.outputAmount.toSignificant(3),
              adjustedInput: taxAdjustedAmounts.adjustedInputAmount?.toSignificant(3),
              adjustedOutput: taxAdjustedAmounts.adjustedOutputAmount?.toSignificant(3),
              finalInput: inputAmount,
              finalOutput: outputAmount
            })

            const base = i18next.t('swapSummary', { inputAmount, inputSymbol, outputAmount, outputSymbol })
            const withRecipient =
              recipient === account
                ? base
                : `${base} ${i18next.t('swapSummaryTo')} ${
                    recipientAddressOrName && isAddress(recipientAddressOrName)
                      ? shortenAddress(recipientAddressOrName)
                      : recipientAddressOrName
                  }`

            const withVersion =
              tradeVersion === Version.v2 ? withRecipient : `${withRecipient} ${i18next.t('swapSummaryOn')} ${(tradeVersion as any).toUpperCase()}`

            addTransaction(response, {
              summary: withVersion
            })

            return response.hash
          })
          .catch((error: any) => {
            // if the user rejected the tx, pass this along
            if (error?.code === 4001) {
              throw new Error(i18next.t('transactionRejected') as string)
            } else {
              // otherwise, the error was unexpected and we need to convey that
              console.error(`Swap failed`, error, methodName, args, value)
              throw new Error(i18next.t('swapFailed', { message: error.message }) as string)
            }
          })
      },
      error: null
    }
  }, [trade, library, account, chainId, recipient, recipientAddressOrName, swapCalls, addTransaction])
}
